import { BookOpen, Hammer, LaptopMinimalCheck, Sprout, WandSparkles, type LucideIcon } from "lucide-react";

import { Category } from "types/category";
import { SubCategory } from "types/subCategory";

type ServiceCategoryConfig = {
    category: Category;
    slug: string;
    Icon: LucideIcon;
    palette: "violet" | "amber" | "green" | "pink" | "teal";
    subcategories: ReadonlyArray<{ key: SubCategory; slug: string }>;
};

/** Single source of truth for the active service categories (catalog, menu, filters). */
export const ACTIVE_SERVICE_CATEGORIES = {
    HOUSEKEEPING: {
        category: Category.HOUSEKEEPING,
        slug: "entretien-domestique",
        Icon: WandSparkles,
        palette: "violet",
        subcategories: [
            { key: SubCategory.CLEANING, slug: "menage" },
            { key: SubCategory.LAUNDRY, slug: "entretien-du-linge" },
        ],
    },
    HANDIWORK: {
        category: Category.HANDIWORK,
        slug: "bricolage",
        Icon: Hammer,
        palette: "amber",
        subcategories: [
            { key: SubCategory.SIMPLE, slug: "petits-travaux-de-bricolage" },
            { key: SubCategory.SMALL_REPAIR, slug: "petites-reparations" },
            { key: SubCategory.INSTALLATION, slug: "pose-et-fixation" },
        ],
    },
    GARDENING: {
        category: Category.GARDENING,
        slug: "jardinage",
        Icon: Sprout,
        palette: "green",
        subcategories: [
            { key: SubCategory.PLANTATION, slug: "plantation-pelouse-et-haie" },
            { key: SubCategory.TERRACE, slug: "entretien-terrasse-et-jardin" },
            { key: SubCategory.TRIMMING, slug: "taille-de-haie" },
            { key: SubCategory.OUTDOOR_CLEANING, slug: "nettoyage-exterieur" },
        ],
    },
    ASSISTANCE: {
        category: Category.ASSISTANCE,
        slug: "assistance",
        Icon: LaptopMinimalCheck,
        palette: "pink",
        subcategories: [
            { key: SubCategory.ADMINISTRATIVE, slug: "administrative" },
            { key: SubCategory.TROUBLESHOOTING, slug: "troubleshooting" },
            { key: SubCategory.INTERNET_USE, slug: "internet-use" },
        ],
    },
    PRIVATE_TUTORING: {
        category: Category.PRIVATE_TUTORING,
        slug: "cours-a-domicile",
        Icon: BookOpen,
        palette: "teal",
        subcategories: [
            { key: SubCategory.HOMEWORK, slug: "soutien-scolaire" },
            { key: SubCategory.FOREIGN_LANGUAGES, slug: "langues-etrangeres" },
            { key: SubCategory.OTHER_LESSON, slug: "autre-lecon-particuliere" },
        ],
    },
} as const satisfies Record<string, ServiceCategoryConfig>;

export type ActiveServiceCategoryKey = keyof typeof ACTIVE_SERVICE_CATEGORIES;

export const ACTIVE_SERVICE_CATEGORY_KEYS = Object.keys(
    ACTIVE_SERVICE_CATEGORIES,
) as ActiveServiceCategoryKey[];

export const ACTIVE_CATEGORIES = ACTIVE_SERVICE_CATEGORY_KEYS.map(
    (key) => ACTIVE_SERVICE_CATEGORIES[key].category,
);

const FALLBACK_VISUAL = ACTIVE_SERVICE_CATEGORIES.HOUSEKEEPING;

export function getCategoryVisual(categoryKey: string) {
    const key = categoryKey.toUpperCase() as ActiveServiceCategoryKey;
    return ACTIVE_SERVICE_CATEGORIES[key] ?? FALLBACK_VISUAL;
}

export function isActiveServiceCategory(categoryKey: string): boolean {
    return categoryKey.toUpperCase() in ACTIVE_SERVICE_CATEGORIES;
}

const ACTIVE_SUBCATEGORIES = ACTIVE_SERVICE_CATEGORY_KEYS.flatMap((key) =>
    ACTIVE_SERVICE_CATEGORIES[key].subcategories.map((sub) => ({ ...sub, categoryKey: key })),
);

/** Lookup the parent category from a subcategory URL slug. */
export const SERVICE_SLUG_TO_CATEGORY: Record<string, ActiveServiceCategoryKey> = Object.fromEntries(
    ACTIVE_SUBCATEGORIES.map((sub) => [sub.slug, sub.categoryKey]),
);

/** Lookup the SubCategory enum value from a subcategory URL slug. */
export const SERVICE_SLUG_TO_SUBCATEGORY: Record<string, SubCategory> = Object.fromEntries(
    ACTIVE_SUBCATEGORIES.map((sub) => [sub.slug, sub.key]),
);
